import { BaseDialog } from "./dialog"
import { useState } from "react"
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { Button } from "../ui/button";
import { DatePicker } from "../ui/date-picker";
import { toast } from "../ui/use-toast";
import { proceedToPaymentForOneTimeContract } from "@/service/weexpertService";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";

export default function ProceedToPaymentPopup({ contractId, contractCreatedAt, onSuccess }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  
  // Convert contract creation date to Date object for comparison
  const minDate = new Date(contractCreatedAt);
  
  // Set default date to today or contract creation date, whichever is later
  const [invoiceIssueDate, setInvoiceIssueDate] = useState(() => {
    const today = new Date();
    return today > minDate ? today : minDate;
  });

  const mutation = useMutation({
    mutationFn: (data) => proceedToPaymentForOneTimeContract(contractId, format(data.invoiceIssueDate, 'yyyy-MM-dd')),
    onSuccess: (response) => {
      toast({
        title: "Sukces",
        description: `Płatność została zainicjowana. Potrącona opłata: ${response.data.data.fee} PLN`,
      });
      setOpen(false);
      // Reset to today or contract creation date, whichever is later
      const today = new Date();
      setInvoiceIssueDate(today > minDate ? today : minDate);
      if (onSuccess) {
        onSuccess();
      }
      queryClient.invalidateQueries(['oneTimeContract', contractId]);
    },
    onError: (error) => {
      toast({
        title: "Błąd",
        description: error.response?.data?.message || "Nie udało się zainicjować płatności",
        variant: "destructive",
      });
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!invoiceIssueDate) {
      toast({
        title: "Błąd",
        description: "Data wystawienia faktury jest wymagana",
        variant: "destructive",
      });
      return;
    }
    
    const data = {
      invoiceIssueDate: invoiceIssueDate
    };
    
    mutation.mutate(data);
  };

  const handleCancel = () => {
    setOpen(false);
    // Reset to today or contract creation date, whichever is later
    const today = new Date();
    setInvoiceIssueDate(today > minDate ? today : minDate);
  };

  return (
    <BaseDialog
      open={open}
      setOpen={setOpen}
      buttonText="Przejdź do płatności"
      dialogTitle="Przejście do płatności"
      className="bg-blue-600 text-white hover:bg-blue-700"
      ommitStyles={true}
    >
      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <div className="space-y-2">
          <DatePicker
            label="Data wystawienia faktury"
            value={invoiceIssueDate}
            date={invoiceIssueDate}
            setDate={setInvoiceIssueDate}
            disabled={mutation.isLoading ? true : (date) => {
              // Disable dates before contract creation
              return date < minDate;
            }}
            fromDate={minDate}
          />
          <p className="text-sm text-gray-600">
            Wybierz datę, na którą ma zostać wystawiona faktura dla tego kontraktu.
          </p>
        </div>
        
        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={mutation.isLoading}
          >
            Anuluj
          </Button>
          <Button
            type="submit"
            disabled={mutation.isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {mutation.isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Przetwarzanie...
              </>
            ) : (
              'Przejdź do płatności'
            )}
          </Button>
        </div>
      </form>
    </BaseDialog>
  )
}